import type {
    HistoryBlobDescriptor,
    HistoryBlobWriteRequest,
    HistoryPersistence,
} from '../HistoryPersistenceTypes';

/**
 * OPFS 历史 blob 写入器配置。
 */
export interface CreateOPFSHistoryBlobWriterOptions {
    /** 根目录名称。 */
    readonly rootDirectoryName?: string;
}

/**
 * 将 blob 内容转换为 Uint8Array。
 *
 * @param content blob 内容。
 * @returns Uint8Array 内容。
 */
function toBytes(content: HistoryBlobWriteRequest['content']): Uint8Array<ArrayBuffer> {
    if (typeof content === 'string') {
        return new TextEncoder().encode(content);
    }

    const source = content instanceof Uint8Array
        ? content
        : new Uint8Array(content);
    const bytes = new Uint8Array(source.byteLength);

    bytes.set(source);

    /**
     * OPFS 和 WebCrypto 的类型定义要求 ArrayBuffer，而不是 SharedArrayBuffer。
     * 这里统一复制到新 ArrayBuffer，避免调用端传入共享缓冲区造成浏览器写入失败。
     */
    return bytes;
}

/**
 * 将 blob 内容转换为 FileSystem 可写入的 ArrayBuffer。
 *
 * @param bytes 字节数组。
 * @returns 独立 ArrayBuffer。
 */
function toWritableArrayBuffer(bytes: Uint8Array<ArrayBuffer>): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

/**
 * 计算 SHA-256 hash。
 *
 * @param bytes 二进制内容。
 * @returns hash 字符串。
 */
async function createSHA256Hash(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
    if (!globalThis.crypto?.subtle) {
        throw new Error('当前浏览器不支持 crypto.subtle，无法为 OPFS 历史 blob 生成校验 hash。');
    }

    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    const hashBytes = Array.from(new Uint8Array(digest));
    const hash = hashBytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');

    return `sha256:${hash}`;
}

/**
 * 获取或创建 OPFS 子目录。
 *
 * @param root 根目录。
 * @param names 子目录名称列表。
 * @returns 最深层目录句柄。
 */
async function ensureDirectory(
    root: FileSystemDirectoryHandle,
    names: readonly string[],
): Promise<FileSystemDirectoryHandle> {
    let current = root;

    for (const name of names) {
        current = await current.getDirectoryHandle(name, { create: true });
    }

    return current;
}

/**
 * 创建 OPFS 历史 blob 写入器。
 *
 * @param options 写入器配置。
 * @returns 可注入 HistoryPersistence 的 blob 写入函数。
 */
export function createOPFSHistoryBlobWriter(
    options: CreateOPFSHistoryBlobWriterOptions = {},
): NonNullable<HistoryPersistence['writeBlob']> {
    const rootDirectoryName = options.rootDirectoryName ?? 'cad-history';

    return async (request: HistoryBlobWriteRequest): Promise<HistoryBlobDescriptor> => {
        if (!navigator.storage?.getDirectory) {
            throw new Error('当前浏览器不支持 OPFS，无法写入历史 blob。');
        }

        const bytes = toBytes(request.content);
        const hash = await createSHA256Hash(bytes);
        const opfsRoot = await navigator.storage.getDirectory();
        const targetDirectory = await ensureDirectory(opfsRoot, [
            rootDirectoryName,
            'blobs',
            hash.slice(7, 15),
        ]);
        const fileName = `${request.blobId}.bin`;
        const fileHandle = await targetDirectory.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();

        try {
            await writable.write(toWritableArrayBuffer(bytes));
        } finally {
            await writable.close();
        }

        return {
            blobId: request.blobId,
            hash,
            byteLength: bytes.byteLength,
            storageTier: 'opfs',
            storageKey: `/${rootDirectoryName}/blobs/${hash.slice(7, 15)}/${fileName}`,
            mediaType: request.mediaType,
        };
    };
}

/**
 * 清空 OPFS 中的撤销重做历史目录。
 *
 * @param options 清理配置。
 * @throws 原样抛出除目录不存在以外的 OPFS 目录获取或删除异常。
 */
export async function clearOPFSHistoryStorage(
    options: CreateOPFSHistoryBlobWriterOptions = {},
): Promise<void> {
    if (!navigator.storage?.getDirectory) {
        return;
    }

    const rootDirectoryName = options.rootDirectoryName ?? 'cad-history';
    const opfsRoot = await navigator.storage.getDirectory();

    try {
        await opfsRoot.removeEntry(rootDirectoryName, { recursive: true });
    } catch (error) {
        if (error instanceof DOMException && error.name === 'NotFoundError') {
            return;
        }

        throw error;
    }
}
